// Sessão de estudo (0.67, texto 05 G3): montar, salvar, repetir e acompanhar.
import { useEffect, useRef, useState } from 'react';
import { Alert, Platform, Pressable, ScrollView, Switch, Text, View } from 'react-native';
import * as Haptics from 'expo-haptics';
import { router } from 'expo-router';
import { useDoc } from '../store/store';
import type { Materia, PassoSessao, PlanoSessao, SessaoPronta } from '../store/types';
import {
  comecarSessao, duplicarSessaoPronta, encerrarSessao, estadoDa, excluirSessaoPronta, materiaDaVez, novaSessaoPronta, pausarOuContinuar, pedirAbrirMateria, proximoPasso,
  salvarSessaoPronta, sessaoAtual, trechosDe, useSessao, verificar,
  diasDoPlanoAte, encerrarPlano, estadoPlano, novoPlano, prolongarPlano, sessoesComPlano,
} from '../lib/sessaoEstudo';
import { cronoAtual, encerrarEstudo, iniciarEstudo, minutosNoDia, useCrono } from '../store/estudos';
import { addDays, brYmd, diaCurto, parseYmd, semanaNaSeg, today, ymd } from '../lib/date';
import { PickerModal } from './Pickers';
import { paraLixeira } from '../lib/lixeira';
import { acaoExternaAberta } from '../lib/ausencia';
import { useJanelasAbertas } from '../lib/telasAbertas';
import { AcoesItem } from './Acoes';
import { Ordenavel, seNaoSegurou } from './Arrastar';
import { useSelecao } from './Selecao';
import { FaixaDesfazer } from './Desfazer';
import { MiniTemporizador } from './MiniTemporizador';
import { ActionSheet, Btn, Chip, Field, Sheet, type Action } from './ui';
import { C, alpha, useCores } from './theme';
import { AvisoGrande } from './PauseOverlay';
import { durHoras as durLabel } from '../lib/time';   // 0.69 (I): em Estudos, "5h" e "1h30" (não "05:00", que parece horário)
import { t } from '../lib/i18n';
import { AlvoGuia } from './guia/Destaque';
import { ROLAGEM_TECLADO } from './teclado';

// ─── 0.69 (F): um cronômetro por vez (Sessão de estudo OU cronômetro simples da matéria) ───
// Causa da contagem dupla: o cronômetro simples (store/estudos.ts, "crono") e a sessão (lib/sessaoEstudo.ts, "atual")
// eram dois relógios independentes; cada um lançava os próprios minutos no histórico. Com os dois correndo, o mesmo
// minuto entrava duas vezes. Agora, para ligar um, o outro precisa estar pausado/encerrado (o app pergunta).

/** ▶ e "Estudar agora" de uma matéria. Com a sessão correndo: na matéria da vez não faz nada (o tempo já conta na sessão);
 *  em outra matéria pergunta se pausa a sessão. */
export function estudarMateria(mid: string, nome: string) {
  const s = sessaoAtual();
  if (!s || s.pausaDesde) { iniciarEstudo(mid, nome); return; }
  if (materiaDaVez(s) === mid) return;
  Alert.alert(t('Há uma sessão em andamento.'), t('Pausar a sessão e estudar só esta?'), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Pausar a sessão'), onPress: () => { pausarOuContinuar(); iniciarEstudo(mid, nome); } }]);
}
/** Pausar/Continuar da sessão. Continuar com o cronômetro simples ligado pergunta antes (encerra e salva o cronômetro). */
export function pausarOuContinuarSessao() {
  const s = sessaoAtual(); if (!s) return;
  if (!s.pausaDesde || !cronoAtual()) { pausarOuContinuar(); return; }
  Alert.alert(t('Há um cronômetro em andamento.'), t('Encerrar e salvar o cronômetro e continuar a sessão?'), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Encerrar e salvar'), onPress: () => { encerrarEstudo(); pausarOuContinuar(); } }]);
}
/** Começar uma sessão com o cronômetro simples ligado: pergunta antes (encerra e salva o cronômetro). */
function comecarSemDobrar(s: SessaoPronta, depois: () => void) {
  const vai = () => { comecarSessao(s); depois(); };
  if (!cronoAtual()) { vai(); return; }
  Alert.alert(t('Há um cronômetro em andamento.'), t('Encerrar e salvar o cronômetro e começar a sessão?'), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Encerrar e salvar'), onPress: () => { encerrarEstudo(); vai(); } }]);
}
/** Tempo sugerido para a matéria na sessão (0.69, F2.7): a meta do dia, ou a meta da semana dividida por 7; sem meta, 1 h. */
export function minutosSugeridos(m?: Materia | null) {
  if (!m?.metaMin) return 60;
  const v = m.metaPor === 'dia' ? m.metaMin : m.metaMin / 7;
  return Math.max(5, Math.round(v / 5) * 5);
}

/** A matéria da vez, atualizada na hora da troca (para a lista mostrar "Em sessão" na matéria certa). */
export function useMateriaDaVez() {
  const s = useSessao();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!s || s.pausaDesde) return;
    const e = estadoDa(s);
    if (e.i < 0 || e.i >= s.passos.length) return;
    const ms = Math.max(1000, Math.min(e.fimTrechoMs - Date.now() + 500, 6 * 3600000));
    const i = setTimeout(() => tick(x => x + 1), ms);
    return () => clearTimeout(i);
  });
  return materiaDaVez(s);
}

/** 0.69 (F2.3): dentro da matéria. Na matéria da vez, o mini cartão "Em sessão · 12 min / 60 · Pausar · Próxima" fica no lugar
 *  do "Estudar agora" (é o mesmo tempo da sessão, contado uma vez). Em outra matéria, com a sessão correndo, mostra a da vez
 *  com o botão "Abrir [matéria]" (F2.6: avisa, não abre sozinho). */
export function MiniSessao({ cor, materiaId, onAbrir }: { cor: string; materiaId: string; onAbrir: (mid: string) => void }) {
  const s = useSessao();
  const [, tick] = useState(0);
  useEffect(() => { if (!s) return; const i = setInterval(() => { verificar(); tick(x => x + 1); }, 15000); return () => clearInterval(i); }, [s?.sid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const e = estadoDa(s);
  const p = s.passos[e.i];
  if (!p || !e.trecho) return null;
  const daVez = p.tipo === 'materia' ? p.materiaId : undefined;
  if (daVez === materiaId) {
    return (
      <View style={{ backgroundColor: alpha(cor, 0.14), borderColor: cor, borderWidth: 1, borderRadius: 16, padding: 12, gap: 8 }}>
        <Text style={{ color: C.text, fontWeight: '800' }}>
          {s.pausaDesde ? '⏸️ ' + t('Sessão pausada') : '📚 ' + t('Em sessão')} · <Text style={{ fontVariant: ['tabular-nums'] }}>{t('{n} min', { n: e.feitoMin })} / {Math.round(p.min)}</Text>
        </Text>
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          <Chip cor={cor} on={false} label={s.pausaDesde ? '▶ ' + t('Continuar') : '⏸ ' + t('Pausar')} onPress={pausarOuContinuarSessao} />
          <Chip cor={cor} on={false} label={'⏭ ' + t('Próxima')} onPress={proximoPasso} />
        </View>
      </View>
    );
  }
  if (!daVez || s.pausaDesde) return null;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: alpha(cor, 0.1), borderRadius: 14, padding: 10 }}>
      <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>📚 {t('Agora: {m}', { m: `${e.trecho.emoji ?? ''} ${e.trecho.nome ?? ''}`.trim() })}</Text>
      <Chip small cor={cor} on label={t('Abrir {nome}', { nome: e.trecho.nome ?? '' })} onPress={() => onAbrir(daVez)} />
    </View>
  );
}

/** Minutos com − e + (padrão do texto 03): anda de 5 em 5 e mostra em horas. */
function Minutos({ valor, onMudar, passo = 5 }: { valor: number; onMudar: (n: number) => void; passo?: number }) {
  const [txt, setTxt] = useState(String(valor));
  useEffect(() => { setTxt(String(valor)); }, [valor]);
  const bt = (rot: string, n: number) => (
    <Pressable onPress={() => onMudar(Math.max(passo, valor + n))} hitSlop={6} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: C.text, fontSize: 20, fontWeight: '700' }}>{rot}</Text>
    </Pressable>
  );
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      {bt('−', -passo)}
      <Field value={txt} keyboardType="number-pad" style={{ width: 70, textAlign: 'center' }}
        onChangeText={x => { setTxt(x); const n = parseInt(x, 10); if (n > 0) onMudar(n); }} />
      {bt('+', passo)}
      <Text style={{ color: C.sub, fontSize: 12 }}>{durLabel(valor)}</Text>
    </View>
  );
}

// ─── 0.72 (texto 11e C): plano com período ───

/** Horas por dia com − e + (padrão do texto 03 A6): anda de 30 em 30 min e mostra "2h", "1h30". */
function HorasDia({ valor, onMudar, cor }: { valor: number; onMudar: (n: number) => void; cor: string }) {
  const bt = (rot: string, n: number) => (
    <Pressable onPress={() => onMudar(Math.min(24 * 60, Math.max(30, valor + n)))} hitSlop={6} accessibilityLabel={rot === '+' ? t('Mais') : t('Menos')}
      style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: C.text, fontSize: 20, fontWeight: '700' }}>{rot}</Text>
    </Pressable>
  );
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      {bt('−', -30)}
      <View style={{ minWidth: 70, paddingVertical: 9, paddingHorizontal: 10, borderRadius: 12, borderWidth: 1, borderColor: alpha(cor, 0.5), alignItems: 'center' }}>
        <Text style={{ color: C.text, fontWeight: '800', fontVariant: ['tabular-nums'] }}>{durLabel(valor)}</Text>
      </View>
      {bt('+', 30)}
    </View>
  );
}

/** Ordem dos dias na tela: de segunda a domingo (ou de domingo a sábado, conforme Ajustes > Calendário). */
const ordemDias = () => (semanaNaSeg() ? [1, 2, 3, 4, 5, 6, 0] : [0, 1, 2, 3, 4, 5, 6]);

/** Nova/Editar sessão: "Plano com período" (liga/desliga), Começa em, Termina em, Horas por dia e Dias da semana. */
function EditorPlano({ plano, cor, onMudar }: { plano?: PlanoSessao; cor: string; onMudar: (p: PlanoSessao) => void }) {
  const [qual, setQual] = useState<'inicio' | 'fim' | null>(null);
  const p = plano ?? { ...novoPlano(), ativo: false };
  const ligado = !!plano?.ativo;
  const mudar = (x: Partial<PlanoSessao>) => onMudar({ ...p, ...x });
  const escolher = (d: Date) => {
    const v = ymd(d);
    if (qual === 'inicio') mudar({ inicio: v, fim: p.fim < v ? v : p.fim });
    else if (qual === 'fim') mudar({ fim: v, inicio: p.inicio > v ? v : p.inicio, encerrado: false });
  };
  const linhaData = (rot: string, v: string, q: 'inicio' | 'fim') => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Text style={{ color: C.text, flex: 1 }}>{rot}</Text>
      <Pressable onPress={() => setQual(q)} hitSlop={6} style={{ paddingVertical: 8, paddingHorizontal: 12, borderRadius: 12, backgroundColor: C.card2 }}>
        <Text style={{ color: cor, fontWeight: '700' }}>📅 {brYmd(v)}</Text>
      </Pressable>
    </View>
  );
  const total = diasDoPlanoAte(p, p.fim);
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 12 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>🎯 {t('Plano com período')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Opcional: prazo, horas por dia e dias da semana.')}</Text>
        </View>
        <Switch value={ligado} trackColor={{ true: cor }} onValueChange={v => mudar({ ativo: v })} />
      </View>
      {ligado && <>
        {linhaData(t('Começa em'), p.inicio, 'inicio')}
        {linhaData(t('Termina em'), p.fim, 'fim')}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ color: C.text, flex: 1 }}>{t('Horas por dia')}</Text>
          <HorasDia valor={p.minDia} cor={cor} onMudar={n => mudar({ minDia: n })} />
        </View>
        <Text style={{ color: C.text }}>{t('Dias da semana')}</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {ordemDias().map(d => (
            <Chip key={d} small cor={cor} on={p.dias.includes(d)} label={diaCurto(d)}
              onPress={() => mudar({ dias: p.dias.includes(d) ? p.dias.filter(x => x !== d) : [...p.dias, d].sort() })} />
          ))}
        </View>
        <Text style={{ color: C.sub, fontSize: 12 }}>
          {t('{n} dias de estudo no plano · {dur} por dia', { n: total, dur: durLabel(p.minDia) })}{'\n'}{t('O plano fica só em Estudos (não vai para o My Planner).')}
        </Text>
      </>}
      {/* a data abre aqui dentro da folha (no iPhone, uma janela por cima de outra só aparece se for filha dela) */}
      {qual && <PickerModal mode="date" cor={cor} value={parseYmd(qual === 'inicio' ? p.inicio : p.fim)} onPick={escolher} onClose={() => setQual(null)} />}
    </View>
  );
}

/** "🎯 Dia 12 de 90 · hoje 2h de 4h" (só os dias da semana marcados contam; "hoje" = todo o estudo do dia, contado uma vez). */
export function LinhaPlano({ plano, cor }: { plano: PlanoSessao; cor: string }) {
  const { sessoes } = useDoc('estudos');
  const crono = useCrono();
  const [, tick] = useState(0);
  useEffect(() => { if (!crono?.inicio) return; const i = setInterval(() => tick(x => x + 1), 30000); return () => clearInterval(i); }, [crono?.inicio]);
  const e = estadoPlano(plano);
  const hoje = minutosNoDia(today(), sessoes);
  let txt: string;
  if (e.fase === 'antes') txt = t('Plano começa em {d}', { d: brYmd(plano.inicio) });
  else if (e.fase === 'terminou') txt = '🏁 ' + t('Seu plano terminou');
  else {
    const dia = e.dia ? t('Dia {n} de {t}', { n: e.dia, t: e.total }) : t('{n} dias de plano', { n: e.total });
    txt = `${dia} · ${e.fase === 'folga' ? t('hoje é folga') : t('hoje {feito} de {meta}', { feito: durLabel(hoje), meta: durLabel(plano.minDia) })}`;
  }
  const pct = Math.min(1, hoje / Math.max(1, plano.minDia));
  return (
    <View style={{ gap: 4 }}>
      <Text style={{ color: C.text, fontSize: 13, fontWeight: '600' }}>🎯 {txt}</Text>
      {e.fase === 'dia' && (
        <View style={{ height: 5, borderRadius: 3, backgroundColor: C.card2 }}>
          <View style={{ height: 5, borderRadius: 3, width: `${Math.round(pct * 100)}%`, backgroundColor: pct >= 1 ? C.ok : cor }} />
        </View>
      )}
    </View>
  );
}

const jaAvisados = new Set<string>();   // "Seu plano terminou": a pergunta aparece uma vez por abertura do app
/** Topo de Estudos: o progresso de cada plano (fora a sessão que está correndo, que já mostra o dela no cartão).
 *  No fim do plano: "Seu plano terminou" com Encerrar ou Prolongar (nova data final). Nada é apagado. */
export function PlanosHoje({ cor, exceto, onAbrir }: { cor: string; exceto?: string; onAbrir?: () => void }) {
  const { prontas } = useDoc('estudos');
  const [prolongar, setProlongar] = useState<SessaoPronta | null>(null);
  const lista = sessoesComPlano(prontas ?? []).filter(s => s.id !== exceto);
  const terminados = lista.filter(s => s.plano && estadoPlano(s.plano).fase === 'terminou');
  const perguntar = (s: SessaoPronta) => Alert.alert('🏁 ' + t('Seu plano terminou'), t('{nome}: encerrar o plano ou prolongar com uma nova data final? Nada é apagado.', { nome: s.nome }), [
    { text: t('Agora não'), style: 'cancel' },
    { text: t('Prolongar'), onPress: () => setProlongar(s) },
    { text: t('Encerrar'), onPress: () => encerrarPlano(s.id) }]);
  const chave = terminados.map(s => s.id).join(',');
  useEffect(() => {
    const s = terminados.find(x => !jaAvisados.has(x.id));
    if (!s) return;
    jaAvisados.add(s.id);
    perguntar(s);
  }, [chave]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lista.length && !prolongar) return null;
  return (
    <View style={{ gap: 8 }}>
      {lista.map(s => s.plano && (
        <Pressable key={s.id} onPress={onAbrir} disabled={!onAbrir}
          style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 14, padding: 12, gap: 6 }}>
          <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{s.nome}</Text>
          <LinhaPlano plano={s.plano} cor={cor} />
          {estadoPlano(s.plano).fase === 'terminou' && (
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Chip small cor={cor} on={false} label={t('Encerrar')} onPress={() => encerrarPlano(s.id)} />
              <Chip small cor={cor} on label={t('Prolongar')} onPress={() => setProlongar(s)} />
            </View>
          )}
        </Pressable>
      ))}
      {prolongar?.plano && <PickerModal mode="date" cor={cor} value={parseYmd(addDays(today() > prolongar.plano.fim ? today() : prolongar.plano.fim, 7))}
        onClose={() => setProlongar(null)}
        onPick={d => { const v = ymd(d); if (v < today()) Alert.alert(t('Escolha hoje ou uma data futura.')); else prolongarPlano(prolongar.id, v); }} />}
    </View>
  );
}

/** Resumo de uma sessão: "📐 Matemática 1 h → ☕ 10 min → 📜 História 45 min". */
export function resumoSessao(passos: PassoSessao[]) {
  return trechosDe(passos).map(x => `${x.emoji ?? ''} ${x.tipo === 'pausa' ? '' : x.nome + ' '}${durLabel(Math.round(x.min))}`.trim()).join(' → ');
}

/** Cartão no topo da aba Estudos durante a sessão. */
export function CartaoSessao({ cor }: { cor: string }) {
  const s = useSessao();
  const { prontas } = useDoc('estudos');
  const [, tick] = useState(0);
  useEffect(() => { if (!s) return; const i = setInterval(() => { verificar(); tick(x => x + 1); }, 15000); return () => clearInterval(i); }, [s?.sid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const e = estadoDa(s);
  const pl = prontas?.find(x => x.id === s.sid)?.plano;
  const plano = pl?.ativo && !pl.encerrado ? pl : null;   // 0.72 (texto 11e C3): "Dia 12 de 90 · hoje 2h de 4h"
  const pausadoMs = s.pausadoMs + (s.pausaDesde ? Date.now() - s.pausaDesde : 0);
  return (
    <View style={{ backgroundColor: alpha(cor, 0.14), borderColor: cor, borderWidth: 1, borderRadius: 18, padding: 14, gap: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 24 }}>{s.pausaDesde ? '⏸️' : '📚'}</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{s.pausaDesde ? t('Sessão pausada') : t('Sessão de estudo')}</Text>
          <Text style={{ color: C.text, fontWeight: '800' }} numberOfLines={1}>{s.nome}</Text>
        </View>
      </View>
      {plano && <LinhaPlano plano={plano} cor={cor} />}
      <MiniTemporizador trechos={trechosDe(s.passos).map(x => ({ ...x, min: Math.round(x.min * 10) / 10 }))} inicioMs={s.inicioMs} pausadoMs={pausadoMs} cor={cor} emojiPausa="☕"
        agoraMs={s.pausaDesde ?? undefined}
        textos={{ proxima: n => t('troca em {n} min', { n }), contaAtiv: x => t('matéria {n} de {t}', { n: x.nAtiv, t: x.totalAtiv }), contaPausa: () => null }} />
      {e.i >= 0 && e.i < s.passos.length && (
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          <Chip cor={cor} on={false} label={s.pausaDesde ? '▶ ' + t('Continuar') : '⏸ ' + t('Pausar')} onPress={pausarOuContinuarSessao} />
          <Chip cor={cor} on={false} label={'⏭ ' + t('Próxima')} onPress={proximoPasso} />
          <Chip cor={cor} on label={'⏹ ' + t('Encerrar')} onPress={() => Alert.alert(t('Encerrar a sessão?'), t('O tempo estudado até agora entra na meta.'), [
            { text: t('Cancelar'), style: 'cancel' },
            { text: t('Encerrar'), onPress: () => { const min = encerrarSessao(); Alert.alert(min ? t('{dur} registrados', { dur: durLabel(min) }) : t('Menos de 1 minuto'), min ? t('Entrou no total da semana.') : t('Nada foi registrado.')); } }])} />
        </View>
      )}
    </View>
  );
}

/** Folha das sessões: lista das sessões salvas (Começar, editar, duplicar, excluir) e o editor. */
export function SessoesSheet({ visivel, cor, onFechar, onComecar }: { visivel: boolean; cor: string; onFechar: () => void;
  onComecar?: (materiaId: string) => void }) {   // 0.69 (F2.2): "Começar" abre a matéria da vez
  const { ficharios, prontas } = useDoc('estudos');
  const lista = prontas ?? [];
  const [ed, setEd] = useState<SessaoPronta | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const materias = ficharios.flatMap(f => f.materias.map(m => ({ m, f })));
  const fechar = () => { setEd(null); onFechar(); };

  const escolherMateria = (onPick: (mid: string) => void) => setMenu({ t: t('Escolha a matéria'), a: materias.length
    ? materias.map(({ m, f }) => ({ label: `${m.icon} ${m.nome}${ficharios.length > 1 ? ` · ${f.nome}` : ''}`, onPress: () => onPick(m.id) }))
    : [{ label: t('Crie uma matéria primeiro'), onPress: () => {} }] });
  const mudarPasso = (i: number, p: Partial<PassoSessao>) => ed && setEd({ ...ed, passos: ed.passos.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const tirarPasso = (i: number) => ed && setEd({ ...ed, passos: ed.passos.filter((_, j) => j !== i) });
  const salvar = () => {
    if (!ed) return;
    if (!ed.passos.some(p => p.tipo === 'materia' && p.materiaId)) return Alert.alert(t('Coloque pelo menos uma matéria.'));
    if (ed.plano?.ativo && !ed.plano.dias.length) return Alert.alert(t('Marque pelo menos um dia da semana no plano.'));
    salvarSessaoPronta({ ...ed, nome: ed.nome.trim() || t('Sessão de estudo'), passos: ed.passos.filter(p => p.tipo === 'pausa' || p.materiaId) });
    setEd(null);
  };
  const excluir = (s: SessaoPronta) => { paraLixeira('estudos', 'lista', s.nome, s, { doc: 'estudos', campo: 'prontas' }); excluirSessaoPronta(s.id); };
  // 0.69 (B1): segurar uma sessão salva entra no modo de seleção (Excluir com confirmação e Desfazer)
  const sel = useSelecao({ itens: lista, chave: s => s.id, cor, genero: 'f', onExcluir: ids => lista.filter(s => ids.includes(s.id)).forEach(excluir) });
  const sugerido = (mid: string) => minutosSugeridos(materias.find(x => x.m.id === mid)?.m);
  const comecar = (s: SessaoPronta) => comecarSemDobrar(s, () => {
    fechar();
    const mid = s.passos.find(p => p.tipo === 'materia' && p.materiaId)?.materiaId;
    if (mid) onComecar?.(mid);
  });

  return (
    <Sheet visible={visivel} onClose={ed ? () => setEd(null) : fechar} title={ed ? (lista.some(x => x.id === ed.id) ? t('Editar sessão') : t('Nova sessão')) : t('Sessão de estudo')}
      right={ed ? <Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable> : null}>
      {!ed ? (
        <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Monte a sessão uma vez (matérias, tempo de cada uma e pausas) e repita nos outros dias tocando em Começar. O tempo estudado entra sozinho na meta.')}</Text>
          {!lista.length && <Text style={{ color: C.sub }}>{t('Nenhuma sessão salva ainda.')}</Text>}
          {sel.barra}
          <Ordenavel itens={lista} chave={s => s.id} desligado gap={8} onOrdem={() => {}} selecao={sel.prop} render={s => (
            <AcoesItem lembrar={{ aba: 'estudos', id: 'sessao:' + s.id, titulo: s.nome }} nome={s.nome} onExcluir={() => excluir(s)} onDuplicar={() => duplicarSessaoPronta(s)}
              menuExtra={[{ label: '✏️ ' + t('Editar'), onPress: () => setEd(JSON.parse(JSON.stringify(s))) }]}>
              <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
                <Text style={{ color: C.text, fontWeight: '800', paddingRight: 24 }}>{s.nome}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={3}>{resumoSessao(s.passos)}</Text>
                {s.plano?.ativo && !s.plano.encerrado && <LinhaPlano plano={s.plano} cor={cor} />}
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Chip cor={cor} on label={'▶ ' + t('Começar')} onPress={seNaoSegurou(() => comecar(s))} />
                  <Chip cor={cor} on={false} label={t('Editar')} onPress={seNaoSegurou(() => setEd(JSON.parse(JSON.stringify(s))))} />
                </View>
              </View>
            </AcoesItem>
          )} />
          <AlvoGuia id="estudos.sessao.nova"><Btn cor={cor} outline label={'+ ' + t('Nova sessão')} onPress={() => setEd(novaSessaoPronta())} /></AlvoGuia>
        </ScrollView>
      ) : (
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 80 }}>
          <Field value={ed.nome} onChangeText={v => setEd({ ...ed, nome: v })} placeholder={t('Nome (ex.: Sessão do mestrado)')} />
          {ed.passos.map((p, i) => (
            <View key={i} style={{ backgroundColor: p.tipo === 'pausa' ? alpha(cor, 0.08) : C.card, borderRadius: 14, padding: 12, gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {p.tipo === 'pausa'
                  ? <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>☕ {t('Pausa')}</Text>
                  : <Pressable onPress={() => escolherMateria(mid => mudarPasso(i, { materiaId: mid, min: sugerido(mid) }))} style={{ flex: 1, flexShrink: 1, minWidth: 0 }}>
                      <Text style={{ color: p.materiaId ? C.text : cor, fontWeight: '700', flexShrink: 1 }}>{p.materiaId ? `${trechosDe([p])[0].emoji} ${trechosDe([p])[0].nome}` : t('Escolher a matéria')} ›</Text>
                    </Pressable>}
                <Pressable hitSlop={6} disabled={i === 0} onPress={() => { const l = [...ed.passos]; [l[i - 1], l[i]] = [l[i], l[i - 1]]; setEd({ ...ed, passos: l }); }}><Text style={{ color: i === 0 ? C.mute : C.sub, fontSize: 16 }}>↑</Text></Pressable>
                <Pressable hitSlop={6} disabled={i === ed.passos.length - 1} onPress={() => { const l = [...ed.passos]; [l[i + 1], l[i]] = [l[i], l[i + 1]]; setEd({ ...ed, passos: l }); }}><Text style={{ color: i === ed.passos.length - 1 ? C.mute : C.sub, fontSize: 16 }}>↓</Text></Pressable>
                <Pressable hitSlop={6} onPress={() => tirarPasso(i)}><Text style={{ color: C.danger }}>✕</Text></Pressable>
              </View>
              <Minutos valor={p.min} onMudar={n => mudarPasso(i, { min: n })} />
            </View>
          ))}
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Btn cor={cor} outline style={{ flex: 1 }} label={'+ ' + t('Matéria')} onPress={() => escolherMateria(mid => setEd({ ...ed, passos: [...ed.passos, { tipo: 'materia', materiaId: mid, min: sugerido(mid) }] }))} />
            <Btn cor={cor} outline style={{ flex: 1 }} label={'+ ☕ ' + t('Pausa')} onPress={() => setEd({ ...ed, passos: [...ed.passos, { tipo: 'pausa', min: 10 }] })} />
          </View>
          {!!ed.passos.length && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Total: {dur}', { dur: durLabel(Math.round(ed.passos.reduce((s, p) => s + p.min, 0))) })}</Text>}
          <EditorPlano plano={ed.plano} cor={cor} onMudar={plano => setEd({ ...ed, plano })} />
        </ScrollView>
      )}
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      <FaixaDesfazer />
    </Sheet>
  );
}

/** Aviso grande na tela da sessão (o mesmo das pausas do My Planner): começo da pausa e troca de matéria. */
export function SessaoOverlay() {
  const s = useSessao();
  const { corDe } = useCores();
  const janelas = useJanelasAbertas();
  const [agora, setAgora] = useState(Date.now());
  const [fechados, setFechados] = useState<Record<string, true>>({});
  useEffect(() => { if (!s) return; const i = setInterval(() => setAgora(Date.now()), 1000); return () => clearInterval(i); }, [s?.sid]); // eslint-disable-line react-hooks/exhaustive-deps
  // 0.69 (F2.6): vibra na troca de passo, mesmo com uma janela aberta (o aviso grande espera a janela fechar)
  const passo = s && !s.pausaDesde ? estadoDa(s, agora).i : -1;
  const passoAntes = useRef<{ chave: string; i: number } | null>(null);
  useEffect(() => {
    const ch = s ? `${s.sid}:${s.inicioMs}` : '';
    const antes = passoAntes.current;
    passoAntes.current = { chave: ch, i: passo };
    if (!antes || antes.chave !== ch || passo <= antes.i || passo <= 0 || !s || passo >= s.passos.length) return;
    if (Platform.OS !== 'web') Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
  }, [passo, s?.sid, s?.inicioMs]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s || s.pausaDesde) return null;
  // 0.69 (C2/D): no iPhone, este aviso (uma janela aberta da raiz) não pode aparecer por cima de outra janela nem da galeria/câmera/arquivos:
  // o sistema recusava, e na volta a janela da matéria ficava "aberta" só para o app (nenhuma matéria abria mais).
  if (janelas > 0 || acaoExternaAberta()) return null;
  const e = estadoDa(s, agora);
  if (!e.trecho || e.i < 0) return null;
  const chave = `${s.sid}:${s.inicioMs}:${e.i}`;
  if (fechados[chave]) return null;
  const segDia = (ms: number) => { const d = new Date(ms); return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(); };
  const cor = corDe('estudos');
  const fechar = () => setFechados(f => ({ ...f, [chave]: true }));
  if (e.trecho.tipo === 'pausa')
    return <AvisoGrande a={{ key: chave, tipo: 'pausa', titulo: s.nome, emoji: '☕', fimSeg: segDia(agora) + Math.round((e.fimTrechoMs - agora) / 1000), cor }} seg={segDia(agora)} onFechar={fechar} />;
  if (e.i > 0 && agora - (e.fimTrechoMs - e.trecho.min * 60000) < 120000)
  {
    const mid = s.passos[e.i]?.materiaId;
    return <AvisoGrande a={{ key: chave, tipo: 'volta', titulo: s.nome, emoji: e.trecho.emoji ?? '📚', fimSeg: segDia(agora) + Math.round((e.fimTrechoMs - agora) / 1000), cor }} seg={segDia(agora)} onFechar={fechar}
      textoVolta={t('Agora: {m}', { m: e.trecho.nome ?? '' })}
      botao={mid ? { label: t('Abrir {nome}', { nome: e.trecho.nome ?? '' }), onPress: () => {
        fechar();
        pedirAbrirMateria(mid);
        router.navigate({ pathname: '/aba/[id]', params: { id: 'estudos', item: mid } } as any);
      } } : undefined} />;
  }
  return null;
}
